import { useEffect, useMemo, useState } from "react";
import { X, Flame, Target, Gift, Sparkles, Trophy, Calendar } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";
import { generateHomeFeed, type FeedItem } from "@/lib/retention/homeFeed";
import { listMissions, completeMission, missionProgress, type MissionId } from "@/lib/retention/missions";
import { loadStreak, pingStreak, nextStreakMilestone } from "@/lib/retention/streaks";
import { trackModeIfChanged } from "@/lib/retention/orchestrator";
import { FeaturedFansPanel } from "@/components/social/FeaturedFansPanel";
import { FanDnaCard } from "@/components/social/FanDnaCard";
import { EmotionalRibbonOrchestrator } from "@/components/orchestration/EmotionalRibbonOrchestrator";
import { GlobalPrestigeBadge } from "@/components/global/GlobalPrestigeBadge";
import { getWorldMemory } from "@/lib/globalDominance/worldMemory";
import { RetentionSprintV1Hub } from "@/components/retention/RetentionSprintV1Hub";
import { NationalPrideSprintV2Hub } from "@/components/nationalPride/NationalPrideSprintV2Hub";
import { ViralSprintV3Hub } from "@/components/viralSprintV3/ViralSprintV3Hub";
import { SprintV4Hub } from "@/components/v4/SprintV4Hub";
import type { PublicEmotionalCtx } from "@/hooks/usePublicEmotionalSignal";

interface Props {
  open: boolean;
  onClose: () => void;
  matchMinute?: number;
  isLive?: boolean;
  emotionalCtx?: PublicEmotionalCtx;
}

const ICONS: Record<string, typeof Flame> = {
  streak: Flame, mission: Target, reward: Gift, recommended_action: Sparkles, trivia: Trophy, upcoming_match: Calendar,
};

export function HomeFeedDrawer({ open, onClose, matchMinute, isLive, emotionalCtx }: Props) {
  const { t } = useLanguage();
  const [tick, setTick] = useState(0);

  useEffect(() => { if (open) { pingStreak(); setTick((x) => x + 1); } }, [open]);

  const feed = useMemo(() => {
    const f = generateHomeFeed({ minute: matchMinute, isLive });
    trackModeIfChanged(f.mode);
    return f;
  }, [matchMinute, isLive, tick]);

  useEffect(() => {
    if (!open) return;
    void trackEvent("homefeed_emotional_view_rendered", { mode: feed.mode, isLive: !!isLive });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, feed.mode]);

  const streak = loadStreak();
  const next = nextStreakMilestone(streak);
  const m = missionProgress();
  const missions = listMissions();

  const onCta = (it: FeedItem) => {
    void trackEvent("retention_campaign_triggered", { id: it.id, kind: it.kind, action: it.action });
    void trackEvent("daily_feed_action_executed", { id: it.id, kind: it.kind, action: it.action, cta: it.ctaKey });
    if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(it.action));
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-3 sm:items-center" onClick={onClose}>
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-border/60 glass shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <div className="text-sm font-bold">{t("home_feed_title")}</div>
            <span className="ml-2 rounded-full bg-accent/40 px-2 py-0.5 text-[10px] font-bold uppercase">{t(`mode_${feed.mode}`)}</span>
          </div>
          <button onClick={onClose} aria-label={t("close")} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto p-3 space-y-3">
          {/* Emotional pulse — single rotating ribbon */}
          {emotionalCtx && (
            <EmotionalRibbonOrchestrator
              ctx={emotionalCtx}
              minute={matchMinute ?? 0}
              isLive={!!isLive}
            />
          )}
          <GlobalPrestigeBadge />
          <FanDnaCard shareable />
          <WorldMemoryStrip />
          {/* SPRINT V1 — Retention modules */}
          <RetentionSprintV1Hub />
          {/* SPRINT V2 — Orgullo Nacional */}
          <NationalPrideSprintV2Hub />
          {/* SPRINT V3 — Viralidad */}
          <ViralSprintV3Hub />
          {/* SPRINT V4 — Adicción */}
          <SprintV4Hub />
          {/* Streak summary */}
          <div className="flex items-center justify-between rounded-xl border border-border/60 bg-background/40 p-3">
            <div>
              <div className="text-[10px] tracking-widest text-muted-foreground">{t("streak_label")}</div>
              <div className="text-2xl font-black tabular-nums">{streak.consecutive_days_active} 🔥</div>
              <div className="text-[10px] text-muted-foreground">{t("streak_longest")}: {streak.longest_streak}</div>
            </div>
            {next && <div className="text-right text-[11px] text-muted-foreground">{t("streak_next_in").replace("{n}", String(next - streak.consecutive_days_active))}</div>}
          </div>

          {/* Missions */}
          <div className="rounded-xl border border-border/60 bg-background/40 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-1 text-xs font-bold"><Target className="h-3 w-3 text-primary" /> {t("missions_title")}</div>
              <div className="text-[10px] text-muted-foreground">{m.done}/{m.total}</div>
            </div>
            <div className="space-y-1">
              {missions.map((mi) => (
                <button key={mi.id}
                  onClick={async () => { if (!mi.completed) { await completeMission(mi.id as MissionId); setTick((x) => x + 1); } }}
                  disabled={mi.completed}
                  className={`flex w-full items-center justify-between rounded-md border px-2 py-1.5 text-left text-xs ${mi.completed ? "border-[oklch(0.78_0.18_145)]/40 bg-[oklch(0.78_0.18_145)]/10 text-muted-foreground" : "border-border/40 bg-background/40 hover:bg-accent/30"}`}>
                  <span>{mi.completed ? "✓ " : "○ "}{t(mi.i18nKey)}</span>
                  <span className="text-[10px] font-bold">+{mi.reward}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Feed items */}
          <div className="space-y-2">
            {feed.items.map((it) => {
              const Icon = ICONS[it.kind] ?? Sparkles;
              return (
                <div key={it.id} className="flex items-center gap-3 rounded-xl border border-border/60 bg-background/40 p-3">
                  <Icon className="h-4 w-4 text-primary" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold">{t(it.titleKey)}</div>
                    {it.bodyKey && <div className="text-[10px] text-muted-foreground">{t(it.bodyKey)}</div>}
                  </div>
                  <button onClick={() => onCta(it)} className="rounded-md bg-primary px-2 py-1 text-[10px] font-bold text-primary-foreground">{t(it.ctaKey)}</button>
                </div>
              );
            })}
          </div>

          {/* Featured Fans — emotional spotlight */}
          <FeaturedFansPanel limit={5} />
        </div>
      </div>
    </div>
  );
}

function WorldMemoryStrip() {
  const m = getWorldMemory();
  const items: Array<{ icon: string; label: string; value: string }> = [];
  if (m.dominantNation) items.push({ icon: "🚩", label: "Nación del día", value: m.dominantNation.label });
  if (m.topFan)         items.push({ icon: "👑", label: "Fan #1",         value: m.topFan.label });
  if (m.chaoticMatch)   items.push({ icon: "💥", label: "Partido caótico", value: m.chaoticMatch.label });
  if (m.tournamentComeback) items.push({ icon: "🚀", label: "Remontada del día", value: m.tournamentComeback.label });
  if (items.length === 0) return null;
  return (
    <div className="rounded-xl border border-border/60 bg-background/40 p-3">
      <div className="mb-2 text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">
        Memoria Mundial
      </div>
      <ul className="space-y-1">
        {items.map((it) => (
          <li key={it.label} className="flex items-center gap-2 text-[11px]">
            <span aria-hidden>{it.icon}</span>
            <span className="text-muted-foreground">{it.label}</span>
            <span className="ml-auto truncate font-bold">{it.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}